import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { EmailService } from '@/services/emailService';
import { isAdmin as isAdminRole, isAssistant as isAssistantRole, isAccountant as isAccountantRole } from '@/utils/roleValidation';

export interface TutorEarning {
  id: string;
  tutor_id: string;
  tutor_name: string;
  tutor_email: string;
  booking_id: string;
  student_name: string;
  subject: string;
  booking_date: string;
  original_price: number;
  currency: string;
  platform_percentage: number;
  tutor_percentage: number;
  tutor_amount: number;
  platform_amount: number;
  status: 'pending' | 'paid' | 'cancelled';
  payment_date: string | null;
  payment_proof: string | null;
  transaction_reference: string | null;
  payment_method: string | null;
  claim_submitted: boolean;
  claim_date: string | null;
  claim_message: string | null;
  claim_resolved: boolean;
  booking_type: 'regular' | 'kashier';
  created: string;
  updated: string;
}

export interface TutorPaymentSettings {
  id?: string;
  tutor_id: string;
  preferred_method: 'bank_transfer' | 'vodafone_cash' | 'instapay' | 'paypal' | 'other';
  bank_name?: string;
  bank_account_number?: string;
  bank_account_name?: string;
  mobile_wallet_number?: string;
  paypal_email?: string;
  other_details?: string;
  minimum_payout_threshold: number;
  notes?: string;
}

export interface PaymentAdjustment {
  id: string;
  tutor_id: string;
  tutor_name: string;
  type: 'bonus' | 'deduction';
  amount: number;
  currency: string;
  reason: string;
  notes?: string;
  created_by: string;
  status: 'pending' | 'paid' | 'cancelled';
  payment_date: string | null;
  created: string;
  updated: string;
}

export interface PaymentClaim {
  id: string;
  earning_id: string;
  tutor_id: string;
  tutor_name: string;
  tutor_email: string;
  message: string;
  admin_response: string | null;
  status: 'pending' | 'responded' | 'resolved';
  responded_at: string | null;
  responded_by: string | null;
  responded_by_name: string | null;
  created: string;
  updated: string;
}

export interface EarningsStats {
  totalEarnings: number;
  pendingAmount: number;
  paidAmount: number;
  pendingCount: number;
  paidCount: number;
  pendingClaims: number;
}

export const getPaymentIndicator = (createdDate: string, status: string) => {
  if (status === 'paid') return { color: 'green', label: 'Paid', bgClass: 'bg-green-500' };
  if (status === 'cancelled') return { color: 'gray', label: 'Cancelled', bgClass: 'bg-gray-400' };

  const created = new Date(createdDate);
  const now = new Date();
  const daysDiff = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

  if (daysDiff < 7) return { color: 'green', label: 'Recent', bgClass: 'bg-green-500' };
  if (daysDiff < 14) return { color: 'yellow', label: '1-2 weeks', bgClass: 'bg-yellow-500' };
  return { color: 'red', label: 'Overdue', bgClass: 'bg-red-500' };
};

export const useTutorEarnings = (tutorId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const effectiveTutorId = tutorId || user?.id;
  const hasStaffAccess = isAdminRole(user) || isAssistantRole(user) || isAccountantRole(user);

  // Fetch earnings (for tutor: their own, for admin/assistant: all or specific tutor)
  const earningsQuery = useQuery({
    queryKey: ['tutor-earnings', effectiveTutorId, hasStaffAccess],
    queryFn: async (): Promise<TutorEarning[]> => {
      try {
        let filter = '';
        if (!hasStaffAccess && effectiveTutorId) {
          filter = `tutor_id = "${effectiveTutorId}"`;
        } else if (tutorId) {
          filter = `tutor_id = "${tutorId}"`;
        }

        const result = await pb.collection('tutor_earnings').getFullList({
          filter,
          sort: '-created'
        });

        return result as unknown as TutorEarning[];
      } catch (error: any) {
        console.error('Error fetching tutor earnings:', error);
        // Return empty array if collection doesn't exist yet
        if (error.status === 404) return [];
        throw error;
      }
    },
    enabled: !!effectiveTutorId || hasStaffAccess,
    staleTime: 30000
  });
  
  // Fetch payment settings
  const settingsQuery = useQuery({
    queryKey: ['tutor-payment-settings', effectiveTutorId],
    queryFn: async (): Promise<TutorPaymentSettings | null> => {
      try {
        if (!effectiveTutorId) return null;
        
        const result = await pb.collection('tutor_payment_settings').getFirstListItem(
          `tutor_id = "${effectiveTutorId}"`
        );
        return result as unknown as TutorPaymentSettings;
      } catch (error: any) {
        if (error.status === 404) return null;
        console.error('Error fetching payment settings:', error);
        return null;
      }
    },
    enabled: !!effectiveTutorId
  });
  
  // Fetch adjustments
  const adjustmentsQuery = useQuery({
    queryKey: ['payment-adjustments', effectiveTutorId, hasStaffAccess],
    queryFn: async (): Promise<PaymentAdjustment[]> => {
      try {
        let filter = '';
        if (!hasStaffAccess && effectiveTutorId) {
          filter = `tutor_id = "${effectiveTutorId}"`;
        } else if (tutorId) {
          filter = `tutor_id = "${tutorId}"`;
        }

        const result = await pb.collection('payment_adjustments').getFullList({
          filter,
          sort: '-created'
        });

        return result as unknown as PaymentAdjustment[];
      } catch (error: any) {
        if (error.status === 404) return [];
        console.error('Error fetching adjustments:', error);
        return [];
      }
    },
    enabled: !!effectiveTutorId || hasStaffAccess
  });

  // Fetch claims for this tutor's earnings
  const claimsQuery = useQuery({
    queryKey: ['payment-claims', effectiveTutorId, hasStaffAccess],
    queryFn: async (): Promise<PaymentClaim[]> => {
      try {
        let filter = '';
        if (!hasStaffAccess && effectiveTutorId) {
          filter = `tutor_id = "${effectiveTutorId}"`;
        } else if (tutorId) {
          filter = `tutor_id = "${tutorId}"`;
        }

        const result = await pb.collection('tutor_payment_claims').getFullList({
          filter,
          sort: '-created'
        });

        return result as unknown as PaymentClaim[];
      } catch (error: any) {
        if (error.status === 404) return [];
        console.error('Error fetching claims:', error);
        return [];
      }
    },
    enabled: !!effectiveTutorId || hasStaffAccess
  });
  
  // Calculate stats
  const stats: EarningsStats = {
    totalEarnings: 0,
    pendingAmount: 0,
    paidAmount: 0,
    pendingCount: 0,
    paidCount: 0,
    pendingClaims: 0
  };
  
  if (earningsQuery.data) {
    earningsQuery.data.forEach(earning => {
      if (earning.status === 'pending') {
        stats.pendingAmount += earning.tutor_amount;
        stats.pendingCount++;
        if (earning.claim_submitted && !earning.claim_resolved) {
          stats.pendingClaims++;
        }
      } else if (earning.status === 'paid') {
        stats.paidAmount += earning.tutor_amount;
        stats.paidCount++;
      }
      stats.totalEarnings += earning.tutor_amount;
    });
  }
  
  // Submit claim mutation - creates a new claim record in tutor_payment_claims
  const submitClaimMutation = useMutation({
    mutationFn: async ({ earningId, message }: { earningId: string; message: string }) => {
      // First, get the earning record to have all the data for the email
      const earning = await pb.collection('tutor_earnings').getOne(earningId);
      
      // Create the claim record in the new claims collection
      try {
        await pb.collection('tutor_payment_claims').create({
          earning_id: earningId,
          tutor_id: earning.tutor_id,
          tutor_name: earning.tutor_name,
          tutor_email: earning.tutor_email,
          message: message || 'Payment reminder requested',
          status: 'pending'
        });
        console.log('✅ Claim record created in tutor_payment_claims');
      } catch (claimError: any) {
        console.warn('⚠️ Failed to create claim in new collection, falling back to legacy:', claimError);
        // Fallback: If collection doesn't exist, update the earning record directly (legacy)
        await pb.collection('tutor_earnings').update(earningId, {
          claim_submitted: true,
          claim_date: new Date().toISOString(),
          claim_message: message
        });
      }
      
      // Also update the earning record for backwards compatibility
      await pb.collection('tutor_earnings').update(earningId, {
        claim_submitted: true,
        claim_date: new Date().toISOString(),
        claim_message: message
      });
      
      // Create audit log
      try {
        await pb.collection('payment_audit_log').create({
          record_id: earningId,
          record_type: 'claim',
          action: 'claim_submitted',
          changed_by: user?.id,
          changed_by_name: user?.name || 'Unknown',
          notes: `Claim submitted: ${message}`
        });
      } catch (e) {
        console.error('Failed to create audit log:', e);
      }
      
      // Send email notification to admins
      try {
        console.log('📧 Sending payment reminder email to admins...');
        const emailResult = await EmailService.sendPaymentReminderToAdmins({
          tutorName: earning.tutor_name,
          tutorEmail: earning.tutor_email,
          earningId: earning.id,
          bookingId: earning.booking_id,
          amount: earning.original_price,
          tutorAmount: earning.tutor_amount,
          subject: earning.subject,
          bookingDate: earning.booking_date,
          claimMessage: message,
          studentName: earning.student_name
        });
        
        if (emailResult.success) {
          console.log('✅ Payment reminder email sent successfully');
        } else {
          console.warn('⚠️ Failed to send payment reminder email:', emailResult.error);
        }
      } catch (emailError) {
        console.error('❌ Error sending payment reminder email:', emailError);
        // Don't fail the claim submission if email fails
      }
    },
    onSuccess: () => {
      toast({ title: 'Claim Submitted', description: 'Your payment reminder has been sent to the admin.' });
      queryClient.invalidateQueries({ queryKey: ['tutor-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['payment-claims'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to submit claim', variant: 'destructive' });
    }
  });
  
  // Update payment settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<TutorPaymentSettings>) => {
      if (settingsQuery.data?.id) {
        return await pb.collection('tutor_payment_settings').update(settingsQuery.data.id, settings);
      } else {
        return await pb.collection('tutor_payment_settings').create({
          tutor_id: effectiveTutorId,
          ...settings
        });
      }
    },
    onSuccess: () => {
      toast({ title: 'Settings Saved', description: 'Your payment settings have been updated.' });
      queryClient.invalidateQueries({ queryKey: ['tutor-payment-settings'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to save settings', variant: 'destructive' });
    }
  });
  
  // Get claims for a specific earning
  const getClaimsForEarning = (earningId: string): PaymentClaim[] => {
    return (claimsQuery.data || []).filter(claim => claim.earning_id === earningId);
  };
  
  // Check if tutor can send another claim (based on last claim status)
  const canSendNewClaim = (earningId: string): boolean => {
    const claims = getClaimsForEarning(earningId);
    if (claims.length === 0) return true;
    // Can send new claim if last claim was responded to
    const lastClaim = claims[0]; // Already sorted by -created
    return lastClaim.status === 'responded' || lastClaim.status === 'resolved';
  };
  
  return {
    earnings: earningsQuery.data || [],
    isLoadingEarnings: earningsQuery.isLoading,
    paymentSettings: settingsQuery.data,
    isLoadingSettings: settingsQuery.isLoading,
    adjustments: adjustmentsQuery.data || [],
    isLoadingAdjustments: adjustmentsQuery.isLoading,
    claims: claimsQuery.data || [],
    isLoadingClaims: claimsQuery.isLoading,
    stats,
    submitClaim: submitClaimMutation.mutate,
    isSubmittingClaim: submitClaimMutation.isPending,
    updateSettings: updateSettingsMutation.mutate,
    isUpdatingSettings: updateSettingsMutation.isPending,
    getClaimsForEarning,
    canSendNewClaim,
    refetch: () => {
      earningsQuery.refetch();
      settingsQuery.refetch();
      adjustmentsQuery.refetch();
      claimsQuery.refetch();
    }
  };
};

// Admin/staff hook for managing all payments
export const useAdminPayments = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const hasStaffAccess = isAdminRole(user) || isAssistantRole(user) || isAccountantRole(user);
  
  // Mark as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: async ({ 
      earningIds, 
      paymentMethod, 
      transactionReference,
      paymentProofFile
    }: { 
      earningIds: string[]; 
      paymentMethod: string;
      transactionReference?: string;
      paymentProofFile?: File;
    }) => {
      for (const earningId of earningIds) {
        const formData = new FormData();
        formData.append('status', 'paid');
        formData.append('payment_date', new Date().toISOString());
        formData.append('payment_method', paymentMethod);
        if (transactionReference) formData.append('transaction_reference', transactionReference);
        if (paymentProofFile) formData.append('payment_proof', paymentProofFile);
        
        await pb.collection('tutor_earnings').update(earningId, formData);
        
        // Create audit log
        try {
          await pb.collection('payment_audit_log').create({
            record_id: earningId,
            record_type: 'earnings',
            action: 'status_changed',
            old_status: 'pending',
            new_status: 'paid',
            changed_by: user?.id,
            changed_by_name: user?.name || 'Admin',
            notes: `Payment made via ${paymentMethod}${transactionReference ? ` (Ref: ${transactionReference})` : ''}`
          });
        } catch (e) {
          console.error('Failed to create audit log:', e);
        }
      }
    },
    onSuccess: () => {
      toast({ title: 'Payments Marked as Paid', description: 'The selected payments have been marked as paid.' });
      queryClient.invalidateQueries({ queryKey: ['tutor-earnings'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to mark as paid', variant: 'destructive' });
    }
  });
  
  // Resolve claim mutation (legacy - for tutor_earnings table)
  const resolveClaimMutation = useMutation({
    mutationFn: async (earningId: string) => {
      await pb.collection('tutor_earnings').update(earningId, {
        claim_resolved: true
      });
      
      try {
        await pb.collection('payment_audit_log').create({
          record_id: earningId,
          record_type: 'earnings',
          action: 'claim_resolved',
          changed_by: user?.id,
          changed_by_name: user?.name || 'Admin',
          notes: 'Claim resolved by admin'
        });
      } catch (e) {
        console.error('Failed to create audit log:', e);
      }
    },
    onSuccess: () => {
      toast({ title: 'Claim Resolved', description: 'The claim has been marked as resolved.' });
      queryClient.invalidateQueries({ queryKey: ['tutor-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['payment-claims'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to resolve claim', variant: 'destructive' });
    }
  });
  
  // Respond to claim mutation - allows admin to respond to a specific claim
  const respondToClaimMutation = useMutation({
    mutationFn: async ({ claimId, response, markResolved }: { claimId: string; response: string; markResolved?: boolean }) => {
      // Update the claim record
      await pb.collection('tutor_payment_claims').update(claimId, {
        admin_response: response,
        status: markResolved ? 'resolved' : 'responded',
        responded_at: new Date().toISOString(),
        responded_by: user?.id,
        responded_by_name: user?.name || 'Admin'
      });
      
      // Get claim to update the parent earning record
      const claim = await pb.collection('tutor_payment_claims').getOne(claimId);
      
      // Get the earning record to get tutor email
      const earning = await pb.collection('tutor_earnings').getOne(claim.earning_id);
      
      // Update the earning record if resolved
      if (markResolved) {
        await pb.collection('tutor_earnings').update(claim.earning_id, {
          claim_resolved: true
        });
      }
      
      // Create audit log
      try {
        await pb.collection('payment_audit_log').create({
          record_id: claimId,
          record_type: 'claim',
          action: markResolved ? 'claim_resolved' : 'claim_responded',
          changed_by: user?.id,
          changed_by_name: user?.name || 'Admin',
          notes: `Admin response: ${response.substring(0, 100)}...`
        });
      } catch (e) {
        console.error('Failed to create audit log:', e);
      }
      
      // Send email notification to tutor
      try {
        const tutorEmail = earning.tutor_email;
        if (tutorEmail) {
          const { EmailService } = await import('@/services/emailService');
          const emailServiceInstance = new EmailService();
          await emailServiceInstance.sendNotificationEmail({
            userEmail: tutorEmail,
            userName: earning.tutor_name || 'Tutor',
            notificationType: 'claim_response',
            title: markResolved ? 'تم حل مطالبتك بالدفع' : 'رد على مطالبتك بالدفع',
            message: `رد المشرف: ${response}\n\nالحالة: ${markResolved ? 'تم الحل' : 'تم الرد'}`,
            createdAt: new Date().toISOString()
          });
          console.log('✅ Email notification sent to tutor:', tutorEmail);
        } else {
          console.warn('⚠️ No tutor email found for earning:', earning.id);
        }
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't throw - the claim update was successful
      }
      
      return claim;
    },
    onSuccess: () => {
      toast({ title: 'Response Sent', description: 'Your response has been sent to the tutor.' });
      queryClient.invalidateQueries({ queryKey: ['tutor-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['payment-claims'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to respond to claim', variant: 'destructive' });
    }
  });
  
  // Create adjustment mutation
  const createAdjustmentMutation = useMutation({
    mutationFn: async (adjustment: Omit<PaymentAdjustment, 'id' | 'created' | 'updated'>) => {
      return await pb.collection('payment_adjustments').create({
        ...adjustment,
        created_by: user?.id
      });
    },
    onSuccess: () => {
      toast({ title: 'Adjustment Created', description: 'The payment adjustment has been created.' });
      queryClient.invalidateQueries({ queryKey: ['payment-adjustments'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create adjustment', variant: 'destructive' });
    }
  });
  
  return {
    markAsPaid: markAsPaidMutation.mutate,
    isMarkingAsPaid: markAsPaidMutation.isPending,
    resolveClaim: resolveClaimMutation.mutate,
    isResolvingClaim: resolveClaimMutation.isPending,
    respondToClaim: respondToClaimMutation.mutate,
    isRespondingToClaim: respondToClaimMutation.isPending,
    createAdjustment: createAdjustmentMutation.mutate,
    isCreatingAdjustment: createAdjustmentMutation.isPending,
    // kept for backward compatibility with callers
    isAdmin: hasStaffAccess
  };
};

// Helper to create earnings after booking
export const createTutorEarning = async (
  tutorId: string,
  tutorName: string,
  tutorEmail: string,
  bookingId: string,
  studentName: string,
  subject: string,
  bookingDate: string,
  originalPrice: number,
  bookingType: 'regular' | 'kashier',
  tutorUserId?: string // The user ID associated with the tutor (for relation field)
) => {
  try {
    const platformPercentage = 20;
    const tutorPercentage = 80;
    const tutorAmount = originalPrice * (tutorPercentage / 100);
    const platformAmount = originalPrice * (platformPercentage / 100);
    
    // Use tutorUserId if provided (for relation to users table), otherwise fall back to tutorId
    const effectiveTutorId = tutorUserId || tutorId;
    
    const recordData = {
      tutor_id: effectiveTutorId,
      tutor_name: tutorName,
      tutor_email: tutorEmail,
      booking_id: bookingId,
      student_name: studentName,
      subject: subject,
      booking_date: bookingDate,
      original_price: originalPrice,
      currency: 'EGP',
      platform_percentage: platformPercentage,
      tutor_percentage: tutorPercentage,
      tutor_amount: tutorAmount,
      platform_amount: platformAmount,
      status: 'pending',
      booking_type: bookingType
    };
    
    console.log('📝 Creating tutor_earnings record with data:', recordData);
    
    const result = await pb.collection('tutor_earnings').create(recordData);
    
    console.log('✅ Tutor earning record created successfully:', result);
    return true;
  } catch (error: any) {
    console.error('❌ Failed to create tutor earning:', error);
    console.error('❌ Error details:', error?.response?.data || error?.message || error);
    return false;
  }
};
